import React, {useState, useEffect, useMemo} from "react";
import {NavLink, useLocation, useNavigate} from "react-router-dom";
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDownOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";
import {useSidebarLayout} from "./Context/SidebarProvider";
import {usePermissions} from "../../context/AuthzProvider";
import {useOrgSettings} from "../../context/OrgSettingsProvider";
import {SidebarOverlay} from "./SidebarOverlay";
import StyledTooltip from "../tooltips/StyledTooltip";
import useSidebarOverlay from "./useSidebarOverlay";
import {isEmpty, map, size} from "underscore";
import {useSidebarCreateButtonStyles} from "./sidebarStyles";

const SidebarCreateButton = () => {
  const {isSidebarExpanded, setCurrentPage} = useSidebarLayout();
  const {canAccessExerciseAndIncident, canAccessExercise, canAccessIncident} =
    usePermissions();
  const {responseWorkflowName} = useOrgSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const {ref, isOpen, toggle, close} = useSidebarOverlay();
  const {classes} = useSidebarCreateButtonStyles();

  const [selectedIndex, setSelectedIndex] = useState(
    canAccessExerciseAndIncident && location.pathname.includes("exercises") ? 1 : 0,
  );

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  const options = useMemo(() => {
    const options = [];

    if (canAccessIncident) {
      options.push({
        title: `Create ${responseWorkflowName.upper}`,
        route: `/${responseWorkflowName.lower}s/wizard`,
        icon: <AssignmentLateOutlinedIcon fontSize="small" />,
      });
    }

    if (canAccessExercise) {
      options.push({
        title: "Create Exercise",
        route: "/exercises/wizard",
        icon: <FitnessCenterOutlinedIcon fontSize="small" />,
      });
    }

    return options;
  }, [responseWorkflowName, canAccessIncident, canAccessExercise]);

  const handleOptionClick = (index) => {
    setSelectedIndex(index);
    close();
    navigate(options[index].route);
    setCurrentPage(options[index].route);
  };

  if (isEmpty(options)) {
    return null;
  }

  const safeIndex = selectedIndex >= size(options) ? 0 : selectedIndex;

  const optionsList = map(options, (option, index) => (
    <MenuItem key={option.route} onClick={() => handleOptionClick(index)}>
      <ListItemIcon>{option.icon}</ListItemIcon>
      <ListItemText primary={option.title} primaryTypographyProps={{variant: "body2"}} />
    </MenuItem>
  ));

  if (!isSidebarExpanded) {
    return (
      <Box display="flex" justifyContent="center" py={1}>
        <StyledTooltip title={options[safeIndex].title} placement="right" opacity={isOpen ? 0 : 1}>
          <IconButton
            ref={ref}
            color="primary"
            onClick={toggle}
            size="small"
            id="sidebar-create-button"
            aria-label="Create"
            aria-haspopup="true"
            aria-expanded={isOpen}
          >
            <AddIcon />
          </IconButton>
        </StyledTooltip>
        <SidebarOverlay open={isOpen} anchorEl={ref.current} onClose={close}>
          {optionsList}
        </SidebarOverlay>
      </Box>
    );
  }

  return (
    <Box px={1} py={1}>
      <ButtonGroup variant="outlined" color="primary" fullWidth size="small">
        <Button
          component={NavLink}
          to={options[safeIndex].route}
          onClick={() => setCurrentPage(options[safeIndex].route)}
          className={classes.primaryButton}
          id="sidebar-create-button"
        >
          <Typography variant="caption" noWrap>
            {options[safeIndex].title}
          </Typography>
        </Button>
        <Button
          ref={ref}
          size="small"
          onClick={toggle}
          className={classes.dropdownButton}
          aria-label="select create option"
          aria-haspopup="true"
          aria-expanded={isOpen}
        >
          <ArrowDropDownIcon fontSize="small" />
        </Button>
      </ButtonGroup>
      <SidebarOverlay open={isOpen} anchorEl={ref.current} onClose={close}>
        {optionsList}
      </SidebarOverlay>
    </Box>
  );
};

export default SidebarCreateButton;
